'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { Users, CalendarCheck, AlertCircle, TrendingUp, Banknote, PackageX } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface KpiData {
  activeCustomers: number;
  expiringThisMonth: number;
  todayAppointments: number;
  pendingVerifications: number;
  monthlyRevenue: number;
  outstandingBalance: number;
  lowStockAlerts: number;
}

export default function DashboardBentoGrid() {
  const [kpi, setKpi] = useState<KpiData>({
    activeCustomers: 0,
    expiringThisMonth: 0,
    todayAppointments: 0,
    pendingVerifications: 0,
    monthlyRevenue: 0,
    outstandingBalance: 0,
    lowStockAlerts: 0,
  });
  const supabase = createClient();

  const fetchKpis = useCallback(async () => {
    const today = new Date().toISOString().slice(0, 10);
    const now = new Date();
    const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const monthEnd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-31`;

    const [
      activeRes,
      todayRes,
      pendingRes,
      revenueRes,
      balanceRes,
      inventoryRes,
    ] = await Promise.all([
      supabase.from('customers').select('id, contract_status, service_start_date, months_active', { count: 'exact' }).eq('contract_status', 'active'),
      supabase.from('appointments').select('id', { count: 'exact', head: true }).eq('scheduled_date', today).neq('status', 'cancelled'),
      supabase.from('appointments').select('id', { count: 'exact', head: true }).eq('receipt_status', 'pending'),
      supabase.from('appointments').select('customer_id').eq('status', 'completed').gte('scheduled_date', monthStart).lte('scheduled_date', monthEnd),
      supabase.from('customers').select('outstanding_balance'),
      supabase.from('filter_inventory').select('stock_quantity, reorder_level'),
    ]);

    let monthlyRevenue = 0;
    if (revenueRes.data && revenueRes.data.length > 0) {
      const customerIds = [...new Set(revenueRes.data.map((r) => r.customer_id).filter(Boolean))];
      if (customerIds.length > 0) {
        const { data: custFees } = await supabase
          .from('customers')
          .select('monthly_fee')
          .in('id', customerIds);
        monthlyRevenue = (custFees || []).reduce((sum, c) => sum + Number(c.monthly_fee), 0);
      }
    }

    const totalOutstanding = (balanceRes.data || []).reduce((sum, c) => sum + Number(c.outstanding_balance), 0);
    const lowStock = (inventoryRes.data || []).filter((f) => f.stock_quantity <= f.reorder_level).length;

    setKpi({
      activeCustomers: activeRes.count || 0,
      expiringThisMonth: 0,
      todayAppointments: todayRes.count || 0,
      pendingVerifications: pendingRes.count || 0,
      monthlyRevenue,
      outstandingBalance: totalOutstanding,
      lowStockAlerts: lowStock,
    });
  }, []);

  useEffect(() => {
    fetchKpis();

    // Real-time: re-fetch KPIs whenever appointments, customers, or inventory change
    const channel = supabase
      .channel('dashboard-kpi-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'appointments' }, () => {
        fetchKpis();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'customers' }, () => {
        fetchKpis();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'filter_inventory' }, () => {
        fetchKpis();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchKpis]);

  const now = new Date();
  const monthName = now.toLocaleString('en-US', { month: 'long' });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
      {/* Hero card — spans 2 cols */}
      <div className="col-span-1 md:col-span-2 lg:col-span-2 xl:col-span-2 2xl:col-span-2 rounded-xl border p-5 card-hover bg-gradient-to-br from-sky-600 to-sky-800">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-600 uppercase tracking-wider text-sky-200 mb-2">Active Customers</p>
            <p className="text-hero-val text-white tabular-nums">{kpi.activeCustomers}</p>
            <p className="text-sm text-sky-200 mt-1">
              {kpi.activeCustomers === 0 ? 'No customers registered yet — add your first client' : `${kpi.activeCustomers} active service contracts`}
            </p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-white">
            <Users size={22} />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-4 text-xs text-sky-200">
          <span>{kpi.activeCustomers} contracts active</span>
          <span>·</span>
          <span>{kpi.expiringThisMonth} expiring this month</span>
          <span>·</span>
          <span>36-mo lock-in tracking ready</span>
        </div>
      </div>

      {/* Today's appointments */}
      <div className="rounded-xl border border-border bg-card p-5 card-hover">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-600 uppercase tracking-wider text-muted-foreground mb-2">Today&apos;s Appointments</p>
            <p className="text-hero-val tabular-nums text-foreground">{kpi.todayAppointments}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {kpi.todayAppointments === 0 ? 'No jobs scheduled today' : `${kpi.todayAppointments} jobs scheduled`}
            </p>
          </div>
          <div className="w-9 h-9 rounded-xl bg-secondary flex items-center justify-center text-primary">
            <CalendarCheck size={18} />
          </div>
        </div>
      </div>

      {/* Pending verifications — warning */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 card-hover">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-600 uppercase tracking-wider text-amber-600 mb-2">Pending Verifications</p>
            <p className="text-hero-val tabular-nums text-amber-700">{kpi.pendingVerifications}</p>
            <p className="text-xs text-amber-600 mt-1">
              {kpi.pendingVerifications === 0 ? 'No unverified receipts' : `${kpi.pendingVerifications} receipts awaiting review`}
            </p>
          </div>
          <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600">
            <AlertCircle size={18} />
          </div>
        </div>
      </div>

      {/* Monthly Revenue */}
      <div className="rounded-xl border border-green-200 bg-green-50 p-5 card-hover">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-600 uppercase tracking-wider text-green-600 mb-2">Monthly Revenue</p>
            <p className="text-hero-val tabular-nums text-green-700">₱{kpi.monthlyRevenue.toLocaleString()}</p>
            <p className="text-xs text-green-600 mt-1">{monthName} {now.getFullYear()}</p>
          </div>
          <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center text-green-600">
            <TrendingUp size={18} />
          </div>
        </div>
      </div>

      {/* Outstanding balances */}
      <div className="rounded-xl border border-border bg-card p-5 card-hover">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-600 uppercase tracking-wider text-muted-foreground mb-2">Outstanding Balances</p>
            <p className="text-hero-val tabular-nums text-foreground">₱{kpi.outstandingBalance.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {kpi.outstandingBalance === 0 ? '0 accounts overdue' : 'Total outstanding across all accounts'}
            </p>
          </div>
          <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center text-muted-foreground">
            <Banknote size={18} />
          </div>
        </div>
      </div>

      {/* Filter stock alert */}
      <div className="rounded-xl border border-red-200 bg-red-50 p-5 card-hover">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-600 uppercase tracking-wider text-red-600 mb-2">Filter Stock Alerts</p>
            <p className="text-hero-val tabular-nums text-red-700">{kpi.lowStockAlerts}</p>
            <p className="text-xs text-red-600 mt-1">
              {kpi.lowStockAlerts === 0 ? 'All filter types adequately stocked' : `${kpi.lowStockAlerts} filter type(s) running low`}
            </p>
          </div>
          <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center text-red-600">
            <PackageX size={18} />
          </div>
        </div>
      </div>
    </div>
  );
}